import { useEffect, useState } from 'react';
import { useIntl } from '@cbhq/i18n';
import { useGetWorkspaceAuditEventsCallback } from '@cbhq/identity-api-hooks/account-management/fetch';

import { logError } from '../../../utils/logging';
import type { AuditEditDetailRowEntry } from '../types';
import {
  formatAuditLogLocation,
  getAuditEventMeta,
  getAuditEventTitle,
} from '../utils/auditLogDisplay';
import { mapAuditEventToRow } from '../utils/helpers';

const RECENT_ACTIVITY_SIZE = 6;

export type UseRecentAuditEventsResult = {
  rows: AuditEditDetailRowEntry[];
  isLoading: boolean;
};

export function useRecentAuditEvents(
  organizationId: string,
  actorId: string,
): UseRecentAuditEventsResult {
  const { formatMessage } = useIntl();
  const fetchAuditEvents = useGetWorkspaceAuditEventsCallback();
  const [rows, setRows] = useState<AuditEditDetailRowEntry[]>([]);
  const [isLoading, setIsLoading] = useState(() => Boolean(organizationId && actorId));

  useEffect(() => {
    let isCurrent = true;

    setRows([]);

    if (!organizationId || !actorId) {
      setIsLoading(false);
      return () => {
        isCurrent = false;
      };
    }

    setIsLoading(true);

    async function fetchRecentAuditEvents() {
      try {
        const response = await fetchAuditEvents({
          organizationId,
          actorId,
          size: RECENT_ACTIVITY_SIZE,
        });

        if (!isCurrent) return;

        if (response instanceof Error) {
          logError(response, { context: 'useRecentAuditEvents' });
          setRows([]);
          return;
        }

        const mappedRows = (response?.events ?? []).map((event) => {
          const auditLog = mapAuditEventToRow(event);
          return {
            eventId: auditLog.eventId,
            eventIconName: getAuditEventMeta(auditLog.eventType)?.iconName ?? 'info',
            eventTitle: getAuditEventTitle(auditLog.eventType, formatMessage),
            eventSubtitle: formatAuditLogLocation(auditLog.actorContext),
            timestamp: auditLog.timestamp,
          };
        });

        setRows(mappedRows);
      } catch (caughtError) {
        if (!isCurrent) return;
        logError(caughtError, { context: 'useRecentAuditEvents' });
        setRows([]);
      } finally {
        if (isCurrent) {
          setIsLoading(false);
        }
      }
    }

    void fetchRecentAuditEvents();

    return () => {
      isCurrent = false;
    };
  }, [actorId, fetchAuditEvents, formatMessage, organizationId]);

  return { rows, isLoading };
}
